<script>
    import modalService from '@/services/core/modal.service';
    import Modal from '@/components/modals/Modal';
    import ButtonSpinner from '@/components/shared/ButtonSpinner';
    import CompanySearchSelect from '@/components/shared/CompanySearchSelect';
    import onboardingService from '@/services/onboarding.service';
    import companiesService from '@/services/companies.service';

    const STEPS = ['Company', 'Workbooks', 'Review'];
    const MAX_FILES = 10;

    export default {
        props: {
            modalData: Object
        },
        components: {
            Modal,
            ButtonSpinner,
            CompanySearchSelect
        },
        data() {
            return {
                steps: STEPS,
                step: 0,
                isNewCompany: false,
                selectedCompany: null,
                newCompanyName: '',
                newUserFullName: '',
                newUserUsername: '',
                newUserEmail: '',
                files: [],
                isSaving: false,
                progress: '',
                createdSession: null,
                error: null
            };
        },
        computed: {
            companyLabel() {
                return this.isNewCompany ? `${this.newCompanyName.trim()} (new)` : this.selectedCompany?.name || '';
            },
            isLastStep() {
                return this.step === this.steps.length - 1;
            }
        },
        methods: {
            apiError(err, fallback) {
                return err?.data?.message || err?.message || fallback;
            },
            close() {
                modalService.closeModal(true);
            },
            companyProblem() {
                if (this.isNewCompany && !this.newCompanyName.trim()) return 'Company name is required.';
                if (this.isNewCompany && !this.newUserUsername.trim()) {
                    return 'A first user is required - the company needs an account to sign in with.';
                }
                if (!this.isNewCompany && !parseInt(this.selectedCompany?.id, 10)) return 'Pick a company.';
                return null;
            },
            next() {
                this.error = this.step === 0 ? this.companyProblem() : null;
                if (!this.error) this.step += 1;
            },
            back() {
                this.error = null;
                this.step -= 1;
            },
            pickFiles(e) {
                const picked = Array.from(e.target.files || []);
                e.target.value = '';
                const names = new Set(this.files.map(f => f.name));
                const added = [...this.files, ...picked.filter(f => !names.has(f.name))];
                this.error = added.length > MAX_FILES ? `A session holds at most ${MAX_FILES} files.` : null;
                this.files = added.slice(0, MAX_FILES);
            },
            removeFile(file) {
                this.files = this.files.filter(f => f !== file);
            },
            // The company is created first and separately: if the session then fails to create,
            // the operator has a real company they can retry against rather than a half-made one.
            async resolveCompanyId() {
                if (!this.isNewCompany) {
                    return parseInt(this.selectedCompany?.id, 10) || null;
                }

                const created = await companiesService.createLean({
                    name: this.newCompanyName,
                    fullName: this.newUserFullName,
                    username: this.newUserUsername,
                    email: this.newUserEmail
                });
                return parseInt(created?.id, 10) || null;
            },
            finish(session) {
                modalService.closeModal(false);
                if (typeof this.modalData?.onCreated === 'function') {
                    this.modalData.onCreated(session);
                }
            },
            async create() {
                this.isSaving = true;
                this.error = null;
                try {
                    if (!this.createdSession) {
                        this.progress = 'Creating the session…';
                        const companyId = await this.resolveCompanyId();
                        if (!companyId) {
                            this.error = 'Could not resolve the company.';
                            return;
                        }
                        this.createdSession = await onboardingService.create({ companyId, targetJobKind: 5 });
                    }

                    const failed = [];
                    for (const [i, file] of this.files.entries()) {
                        this.progress = `Uploading ${file.name} (${i + 1} of ${this.files.length})…`;
                        try {
                            await onboardingService.addFile(this.createdSession.id, file);
                        } catch (err) {
                            failed.push({ file, message: `${file.name}: ${this.apiError(err, 'upload failed')}` });
                        }
                    }
                    if (failed.length) {
                        this.files = failed.map(f => f.file);
                        this.error = `The session was created, but some files did not upload. ${failed.map(f => f.message).join('; ')}`;
                        return;
                    }
                    this.finish(this.createdSession);
                } catch (err) {
                    this.error = this.apiError(err, 'Could not create session.');
                } finally {
                    this.isSaving = false;
                    this.progress = '';
                }
            }
        }
    };
</script>

<template>
    <Modal>
        <form class="gsb-form" @submit.prevent="isLastStep ? create() : next()">
            <div class="modal-content">
                <div class="modal-header">
                    <h3 class="modal-title">New gear-up session</h3>
                </div>
                <div class="modal-body">
                    <ol class="list-unstyled d-flex gap-2 mb-3" aria-label="Steps">
                        <li
                            v-for="(label, i) in steps"
                            :key="label"
                            class="flex-fill small px-2 py-1 rounded"
                            :class="i === step ? 'bg-primary text-white' : i < step ? 'bg-success-subtle' : 'bg-body-tertiary text-muted'"
                            :aria-current="i === step ? 'step' : null"
                        >
                            {{ i + 1 }}. {{ label }}
                        </li>
                    </ol>

                    <div v-if="error" class="alert alert-danger py-2">{{ error }}</div>

                    <template v-if="step === 0">
                        <p class="text-muted small mb-3">
                            A company can have as many sessions as you need; close one whenever it is done.
                        </p>
                        <div class="mb-3">
                            <div class="form-check form-check-inline">
                                <input
                                    id="onboardingCompanyExisting"
                                    v-model="isNewCompany"
                                    class="form-check-input"
                                    type="radio"
                                    :value="false"
                                />
                                <label class="form-check-label" for="onboardingCompanyExisting">Existing company</label>
                            </div>
                            <div class="form-check form-check-inline">
                                <input
                                    id="onboardingCompanyNew"
                                    v-model="isNewCompany"
                                    class="form-check-input"
                                    type="radio"
                                    :value="true"
                                />
                                <label class="form-check-label" for="onboardingCompanyNew">New company</label>
                            </div>
                        </div>

                        <div v-if="!isNewCompany" class="mb-3">
                            <label class="form-label" for="onboardingNewCompany">Company</label>
                            <CompanySearchSelect id="onboardingNewCompany" v-model="selectedCompany" input-id="onboardingNewCompany" />
                        </div>

                        <template v-else>
                            <div class="mb-3">
                                <label class="form-label" for="onboardingCompanyName">Company name</label>
                                <input
                                    id="onboardingCompanyName"
                                    v-model="newCompanyName"
                                    class="form-control"
                                    type="text"
                                    placeholder="Acme Oil"
                                />
                            </div>
                            <div class="mb-2">
                                <label class="form-label" for="onboardingUserFullName">First user</label>
                                <input
                                    id="onboardingUserFullName"
                                    v-model="newUserFullName"
                                    class="form-control mb-2"
                                    type="text"
                                    placeholder="Full name"
                                />
                                <input
                                    id="onboardingUserUsername"
                                    v-model="newUserUsername"
                                    class="form-control mb-2"
                                    type="text"
                                    placeholder="Username (required, unique across all companies)"
                                />
                                <input
                                    id="onboardingUserEmail"
                                    v-model="newUserEmail"
                                    class="form-control"
                                    type="email"
                                    placeholder="Email"
                                />
                            </div>
                            <p class="text-muted small mb-0">
                                Everything else — units, billing, alert rules, feature flags — is created from the
                                standard default set and can be changed on the company page afterwards.
                            </p>
                        </template>
                    </template>

                    <template v-else-if="step === 1">
                        <p class="text-muted small mb-3">
                            Add the customer's workbooks now, or skip and drop them later in the session or its
                            Slack thread. Each file is read and planned as soon as it lands.
                        </p>
                        <label class="btn btn-outline-primary btn-sm mb-3" for="onboardingWizardFiles">
                            Add workbooks…
                        </label>
                        <input
                            id="onboardingWizardFiles"
                            class="d-none"
                            type="file"
                            multiple
                            accept=".csv,.xlsx,.xls"
                            @change="pickFiles"
                        />
                        <ul v-if="files.length" class="list-group list-group-flush small">
                            <li
                                v-for="file in files"
                                :key="file.name"
                                class="list-group-item d-flex justify-content-between align-items-center px-0"
                            >
                                {{ file.name }}
                                <button
                                    class="btn btn-link btn-sm text-danger p-0"
                                    type="button"
                                    :aria-label="`Remove ${file.name}`"
                                    @click="removeFile(file)"
                                >
                                    Remove
                                </button>
                            </li>
                        </ul>
                        <p v-else class="small text-muted mb-0">No workbooks yet.</p>
                    </template>

                    <template v-else>
                        <dl class="row small mb-0">
                            <dt class="col-sm-4">Company</dt>
                            <dd class="col-sm-8">{{ companyLabel }}</dd>
                            <template v-if="isNewCompany">
                                <dt class="col-sm-4">First user</dt>
                                <dd class="col-sm-8">{{ newUserUsername }}<span v-if="newUserEmail"> · {{ newUserEmail }}</span></dd>
                            </template>
                            <dt class="col-sm-4">Workbooks</dt>
                            <dd class="col-sm-8">{{ files.length ? files.map(f => f.name).join(', ') : 'None yet' }}</dd>
                            <dt class="col-sm-4">Imports into</dt>
                            <dd class="col-sm-8">BatteryImport (assets), People</dd>
                        </dl>
                        <p v-if="progress" class="small text-muted mt-3 mb-0">{{ progress }}</p>
                    </template>
                </div>
                <div class="modal-footer">
                    <button class="btn btn-outline-secondary" type="button" :disabled="isSaving" @click="close">
                        Cancel
                    </button>
                    <button v-if="step > 0 && !createdSession" class="btn btn-outline-secondary" type="button" :disabled="isSaving" @click="back">
                        Back
                    </button>
                    <button v-if="createdSession" class="btn btn-outline-secondary" type="button" :disabled="isSaving" @click="finish(createdSession)">
                        Open session
                    </button>
                    <button class="btn btn-primary" type="submit" :disabled="isSaving">
                        <ButtonSpinner v-if="isSaving" />
                        {{ isLastStep ? (createdSession ? 'Retry uploads' : 'Create session') : 'Next' }}
                    </button>
                </div>
            </div>
        </form>
    </Modal>
</template>
